import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  X,
  Calendar,
  User,
  Building2,
  Video,
  FileText,
  XCircle,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Sparkles,
  CalendarClock,
  TrendingUp,
  UserCheck,
  Brain,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Markdown } from '../ui/Markdown';
import { useAppointmentsStore } from '../../store/appointmentsStore';
import { useShallow } from 'zustand/react/shallow';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAskOtto } from '../../hooks/useAskOtto';
import { meetingReportRequest } from '../../lib/otto/missionRequests';
import { sensitive } from '../../lib/presentationMode';
import { useMeetingReport } from '../../hooks/useMeetingReports';
import { useRescheduleBase } from '../../hooks/useRescheduleBase';
import { rescheduleUrlOf } from '../../lib/appointments/rescheduleLink';
import { useCanUseTelephony } from '../../lib/telephonyAccess';
import { CallButton } from '../telephony/CallButton';
import { SlotPicker } from './SlotPicker';
import type { Appointment, AppointmentStatus, TimeSlot } from '../../types/appointments';
import { APPOINTMENT_STATUS_LABELS, APPOINTMENT_STATUS_COLORS } from '../../types/appointments';

interface AppointmentDetailSidebarProps {
  appointmentId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AppointmentDetailSidebar({ appointmentId, isOpen, onClose }: AppointmentDetailSidebarProps) {
  const {
    openAccountSidebar,
    openContactSidebar,
    openOpportunitySidebar,
    openLeadSidebar,
    openMeetingReportSidebar,
  } = useSidebar();
  // « Générer le CR » devient une mission ouverte dans le Bureau (plus de chat, 12/10/2026).
  const askOtto = useAskOtto();
  const canTelephony = useCanUseTelephony();

  const {
    cancelAppointment,
    updateAppointmentStatus,
    updateAppointmentNotes,
    rescheduleAppointment,
    fetchAvailableSlots,
    availableSlots,
    slotsLoading,
    loading: storeLoading,
  } = useAppointmentsStore(useShallow(s => ({
    cancelAppointment: s.cancelAppointment,
    updateAppointmentStatus: s.updateAppointmentStatus,
    updateAppointmentNotes: s.updateAppointmentNotes,
    rescheduleAppointment: s.rescheduleAppointment,
    fetchAvailableSlots: s.fetchAvailableSlots,
    availableSlots: s.availableSlots,
    slotsLoading: s.slotsLoading,
    loading: s.loading,
  })));

  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(false);
  const rescheduleBase = useRescheduleBase();
  const [opportunity, setOpportunity] = useState<{ id: string; name: string; amount: number | null } | null>(null);
  const [lead, setLead] = useState<{ id: string; company_name: string } | null>(null);
  const [notes, setNotes] = useState<any[]>([]);
  const [meetingReports, setMeetingReports] = useState<any[]>([]);

  // Action states
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleSlot, setRescheduleSlot] = useState<TimeSlot | null>(null);

  const { hasReport, loading: crLoading } = useMeetingReport({
    appointmentId: appointmentId,
  });

  useEffect(() => {
    if (!appointmentId || !isOpen) {
      setAppointment(null);
      setOpportunity(null);
      setLead(null);
      setNotes([]);
      setMeetingReports([]);
      setShowCancelConfirm(false);
      setShowReschedule(false);
      return;
    }

    setLoading(true);
    supabase
      .from('appointments')
      .select('*, contact:crm_contacts(id, first_name, last_name, email, phone), account:crm_accounts(id, name), appointment_type:appointment_types(*)')
      .eq('id', appointmentId)
      .single()
      .then(async ({ data }) => {
        if (!data) { setLoading(false); return; }
        setAppointment(data as any);

        // Supabase query builders are PromiseLike (thenables), not full Promises
        const promises: PromiseLike<unknown>[] = [];

        // Fetch opportunity if linked
        if (data.opportunity_id) {
          promises.push(
            supabase.from('crm_opportunities').select('id, name, amount').eq('id', data.opportunity_id).single()
              .then(r => setOpportunity(r.data))
          );
        }

        // Fetch lead if linked
        if (data.onboarding_lead_id) {
          promises.push(
            supabase.from('onboarding_leads').select('id, company_name').eq('id', data.onboarding_lead_id).single()
              .then(r => setLead(r.data))
          );
        }

        // Fetch notes linked to this appointment
        promises.push(
          supabase.from('crm_notes').select('id, content, note_type, created_at, source, tags')
            .eq('appointment_id', data.id)
            .order('created_at', { ascending: false })
            .limit(5)
            .then(r => setNotes(r.data || []))
        );

        // Fetch meeting reports for this appointment
        promises.push(
          supabase.from('crm_meeting_reports').select('id, cr_title, status, created_at, transcript_fetched_at')
            .eq('appointment_id', data.id)
            .order('created_at', { ascending: false })
            .limit(5)
            .then(r => setMeetingReports(r.data || []))
        );

        await Promise.all(promises);
        setLoading(false);
      });
  }, [appointmentId, isOpen]);

  if (!appointmentId) return null;

  const contact = (appointment as any)?.contact;
  const account = (appointment as any)?.account;

  const rescheduleUrl = rescheduleUrlOf(rescheduleBase, appointment?.reschedule_token);

  const handleCancel = async () => {
    if (!appointment) return;
    const success = await cancelAppointment(appointment.id, cancelReason);
    if (success) {
      setShowCancelConfirm(false);
      setCancelReason('');
      // Reload
      const { data } = await supabase.from('appointments').select('*, contact:crm_contacts(id, first_name, last_name, email, phone), account:crm_accounts(id, name), appointment_type:appointment_types(*)').eq('id', appointment.id).single();
      if (data) setAppointment(data as any);
    }
  };

  const handleStatusChange = async (status: AppointmentStatus) => {
    if (!appointment) return;
    const success = await updateAppointmentStatus(appointment.id, status);
    if (success) {
      const { data } = await supabase.from('appointments').select('*, contact:crm_contacts(id, first_name, last_name, email, phone), account:crm_accounts(id, name), appointment_type:appointment_types(*)').eq('id', appointment.id).single();
      if (data) setAppointment(data as any);
    }
  };

  const handleSaveNotes = async () => {
    if (!appointment) return;
    const success = await updateAppointmentNotes(appointment.id, notesText);
    if (success) {
      setEditingNotes(false);
      setAppointment({ ...appointment, meeting_notes: notesText });
    }
  };

  const handleGenerateCR = () => {
    if (!appointment) return;
    const contactName = contact
      ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
      : 'Contact inconnu';
    const message = meetingReportRequest({
      appointmentId: appointment.id,
      contactName,
      accountName: account?.name ?? null,
      notes: appointment.meeting_notes,
    });
    void askOtto.start(message, account ? { id: account.id, name: account.name } : null).then((ok) => {
      if (ok) onClose();
    });
  };

  const handleRescheduleOpen = () => {
    if (!appointment) return;
    setShowReschedule(true);
    setShowCancelConfirm(false);
    setRescheduleSlot(null);
    const apptDate = format(new Date(appointment.start_time), 'yyyy-MM-dd');
    setRescheduleDate(apptDate);
    if (appointment.appointment_type_id) {
      const dateTo = format(addDays(new Date(apptDate), 6), 'yyyy-MM-dd');
      fetchAvailableSlots(appointment.appointment_type_id, apptDate, dateTo, true);
    }
  };

  const handleRescheduleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!appointment) return;
    const newDate = e.target.value;
    setRescheduleDate(newDate);
    setRescheduleSlot(null);
    if (appointment.appointment_type_id) {
      const dateTo = format(addDays(new Date(newDate), 6), 'yyyy-MM-dd');
      fetchAvailableSlots(appointment.appointment_type_id, newDate, dateTo, true);
    }
  };

  const handleRescheduleConfirm = async () => {
    if (!appointment || !rescheduleSlot) return;
    const success = await rescheduleAppointment(appointment.id, rescheduleSlot.start);
    if (success) {
      setShowReschedule(false);
      setRescheduleSlot(null);
      const { data } = await supabase.from('appointments').select('*, contact:crm_contacts(id, first_name, last_name, email, phone), account:crm_accounts(id, name), appointment_type:appointment_types(*)').eq('id', appointment.id).single();
      if (data) setAppointment(data as any);
    }
  };

  const statusActions: { status: AppointmentStatus; label: string; icon: React.ReactNode }[] = [
    { status: 'confirmed', label: 'Confirmer', icon: <CheckCircle className="h-4 w-4" /> },
    { status: 'completed', label: 'Terminer', icon: <CheckCircle className="h-4 w-4" /> },
    { status: 'no_show', label: 'Absent', icon: <AlertCircle className="h-4 w-4" /> },
  ];

  const CR_STATUS_BADGE: Record<string, { label: string; color: string }> = {
    draft: { label: 'Brouillon', color: 'bg-neutral-100 text-neutral-600' },
    transcript_fetched: { label: 'Transcript', color: 'bg-blue-50 text-[#0066FF]' },
    cr_generated: { label: 'Genere', color: 'bg-violet-100 text-violet-700' },
    actions_validated: { label: 'Valide', color: 'bg-emerald-100 text-emerald-700' },
    email_sent: { label: 'Envoye', color: 'bg-emerald-100 text-emerald-700' },
    error: { label: 'Erreur', color: 'bg-red-100 text-red-700' },
    no_meeting: { label: 'Non tenue', color: 'bg-amber-100 text-amber-700' },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/30 z-40"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="absolute right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex-shrink-0 border-b border-neutral-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-semibold text-neutral-900 truncate">
                      {appointment?.title || 'Chargement...'}
                    </h2>
                    {appointment && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${APPOINTMENT_STATUS_COLORS[appointment.status]}`}>
                        {APPOINTMENT_STATUS_LABELS[appointment.status]}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={onClose} className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {loading && !appointment ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                </div>
              ) : appointment ? (
                <>
                  {/* Date & Time */}
                  <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-blue-400 mt-0.5" />
                      <div>
                        <p className="font-medium text-neutral-900">
                          {format(new Date(appointment.start_time), "EEEE d MMMM yyyy", { locale: fr })}
                        </p>
                        <p className="text-sm text-neutral-500">
                          {format(new Date(appointment.start_time), 'HH:mm')} - {format(new Date(appointment.end_time), 'HH:mm')} ({appointment.duration_minutes} min)
                        </p>
                      </div>
                    </div>
                    {appointment.appointment_type && (
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: appointment.appointment_type.color || '#3B82F6' }} />
                        <span className="text-sm text-neutral-600">{appointment.appointment_type.name}</span>
                      </div>
                    )}
                    {appointment.google_meet_link && (
                      <a href={appointment.google_meet_link} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-[#0066FF] hover:underline">
                        <Video className="h-4 w-4" /> Rejoindre Google Meet <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {rescheduleUrl && (
                      <a href={rescheduleUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-[#0066FF] hover:underline">
                        <ExternalLink className="h-4 w-4" /> Lien de replanification
                      </a>
                    )}
                  </div>

                  {appointment.description && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">Description</h3>
                      <p className="text-sm text-neutral-700">{appointment.description}</p>
                    </div>
                  )}

                  {/* Contact */}
                  {contact && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5" /> Contact
                      </h3>
                      <div className="bg-white rounded-lg border border-neutral-200">
                        <button
                          onClick={() => { onClose(); setTimeout(() => openContactSidebar(contact.id), 100); }}
                          className="flex items-start gap-3 w-full p-2.5 rounded-lg hover:bg-blue-50 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-[#0066FF] text-xs font-medium flex-shrink-0">
                            {contact.first_name?.[0]}{contact.last_name?.[0]}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-neutral-900 truncate">{contact.first_name} {contact.last_name}</p>
                            {contact.email && <p className="text-xs text-neutral-500 truncate">{contact.email}</p>}
                          </div>
                          <ExternalLink className="w-4 h-4 text-neutral-400 flex-shrink-0 mt-1" />
                        </button>
                        {contact.phone && (
                          <div className="flex items-center gap-2 border-t border-neutral-100 px-2.5 py-1.5">
                            <a href={`tel:${contact.phone}`} className="text-xs text-neutral-600 hover:text-[#0066FF] hover:underline truncate">
                              {contact.phone}
                            </a>
                            {canTelephony && (
                              <CallButton number={contact.phone} contactId={contact.id} accountId={account?.id ?? null} className="ml-auto" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Compte */}
                  {account && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5" /> Compte
                      </h3>
                      <button
                        onClick={() => { onClose(); setTimeout(() => openAccountSidebar(account.id), 100); }}
                        className="flex items-center gap-3 w-full p-2.5 bg-white rounded-lg border border-neutral-200 hover:bg-emerald-50 hover:border-emerald-200 transition-colors text-left"
                      >
                        <div className="p-1.5 bg-emerald-100 rounded-lg">
                          <Building2 className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-neutral-900 truncate">{account.name}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-neutral-400" />
                      </button>
                    </div>
                  )}

                  {/* Opportunite */}
                  {opportunity && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <TrendingUp className="w-3.5 h-3.5" /> Opportunite
                      </h3>
                      <button
                        onClick={() => { onClose(); setTimeout(() => openOpportunitySidebar(opportunity.id), 100); }}
                        className="flex items-center gap-3 w-full p-2.5 bg-white rounded-lg border border-neutral-200 hover:bg-purple-50 hover:border-purple-200 transition-colors text-left"
                      >
                        <div className="p-1.5 bg-purple-100 rounded-lg">
                          <TrendingUp className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-neutral-900 truncate">{opportunity.name}</p>
                          {opportunity.amount && <p className="text-xs text-neutral-500">{sensitive(opportunity.amount.toLocaleString())}€</p>}
                        </div>
                        <ExternalLink className="w-4 h-4 text-neutral-400" />
                      </button>
                    </div>
                  )}

                  {/* Lead */}
                  {lead && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <UserCheck className="w-3.5 h-3.5" /> Lead Onboarding
                      </h3>
                      <button
                        onClick={() => { onClose(); setTimeout(() => openLeadSidebar(lead.id), 100); }}
                        className="flex items-center gap-3 w-full p-2.5 bg-white rounded-lg border border-neutral-200 hover:bg-amber-50 hover:border-amber-200 transition-colors text-left"
                      >
                        <div className="p-1.5 bg-amber-100 rounded-lg">
                          <UserCheck className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-neutral-900 truncate">{lead.company_name}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-neutral-400" />
                      </button>
                    </div>
                  )}

                  {/* Comptes-rendus */}
                  {meetingReports.length > 0 && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" /> Comptes-rendus ({meetingReports.length})
                      </h3>
                      <div className="space-y-2">
                        {meetingReports.map((cr: any) => {
                          const badge = CR_STATUS_BADGE[cr.status] || CR_STATUS_BADGE.draft;
                          return (
                            <button
                              key={cr.id}
                              onClick={() => { onClose(); setTimeout(() => openMeetingReportSidebar(cr.id), 100); }}
                              className="flex items-start gap-3 w-full p-2.5 bg-white rounded-lg border border-neutral-200 hover:bg-violet-50 hover:border-violet-200 transition-colors text-left"
                            >
                              <div className="p-1.5 bg-violet-100 rounded-lg">
                                <FileText className="w-4 h-4 text-violet-600" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-neutral-900 truncate">{cr.cr_title || 'CR sans titre'}</p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${badge.color}`}>{badge.label}</span>
                                  {cr.transcript_fetched_at && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-[#0066FF]">Transcript</span>
                                  )}
                                </div>
                              </div>
                              <ExternalLink className="w-4 h-4 text-neutral-400 flex-shrink-0 mt-1" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Generate CR section */}
                  {!crLoading && !hasReport && meetingReports.length === 0 && (
                    <div className="bg-violet-50 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-semibold text-violet-500 uppercase tracking-wider flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5" /> Compte-rendu
                        </h3>
                        <button
                          onClick={handleGenerateCR}
                          disabled={askOtto.pending}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-60"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          {askOtto.pending ? 'Mission en création…' : 'Générer le CR'}
                        </button>
                      </div>
                      {askOtto.error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{askOtto.error}</p>}
                    </div>
                  )}

                  {/* Brief IA pré-RDV (loop-prebrief) — mis en avant, avant les notes */}
                  {notes.filter((n) => n.source === 'agent' && (n.tags || []).includes('prebrief')).map((brief) => (
                    <div key={brief.id} className="bg-white rounded-xl border border-indigo-100 overflow-hidden">
                      <h3 className="text-xs font-semibold text-indigo-500 uppercase tracking-wider px-4 py-2.5 bg-indigo-50/50 border-b border-indigo-100/70 flex items-center gap-1.5">
                        <Brain className="w-3.5 h-3.5" /> Brief IA
                      </h3>
                      <div data-sensitive-text="" className="max-h-72 overflow-y-auto px-4 py-3">
                        <Markdown className="text-sm text-neutral-700">{brief.content}</Markdown>
                      </div>
                    </div>
                  ))}

                  {/* Notes CRM (hors brief IA, remonté au-dessus) */}
                  {notes.filter((n) => !(n.source === 'agent' && (n.tags || []).includes('prebrief'))).length > 0 && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" /> Notes CRM
                      </h3>
                      <div className="space-y-2">
                        {notes.filter((note) => !(note.source === 'agent' && (note.tags || []).includes('prebrief'))).map((note) => (
                          <div key={note.id} className="p-2.5 bg-white rounded-lg border border-neutral-200">
                            <p data-sensitive-text="" className="text-sm text-neutral-700 line-clamp-2">{note.content}</p>
                            <p className="text-xs text-neutral-400 mt-1">
                              {format(new Date(note.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Meeting notes */}
                  <div className="bg-neutral-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Notes de reunion</h3>
                      {!editingNotes && (
                        <button
                          onClick={() => { setNotesText(appointment.meeting_notes || ''); setEditingNotes(true); }}
                          className="text-xs text-[#0066FF] hover:underline"
                        >
                          {appointment.meeting_notes ? 'Modifier' : 'Ajouter'}
                        </button>
                      )}
                    </div>
                    {editingNotes ? (
                      <div className="space-y-2">
                        <textarea
                          value={notesText}
                          onChange={(e) => setNotesText(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                          placeholder="Notes de la reunion..."
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditingNotes(false)} className="px-3 py-1.5 text-sm text-neutral-600 hover:text-neutral-800">Annuler</button>
                          <button onClick={handleSaveNotes} disabled={storeLoading}
                            className="px-3 py-1.5 bg-[#0066FF] text-white rounded-lg text-sm hover:bg-[#0052CC] disabled:opacity-50">
                            {storeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-neutral-600">{appointment.meeting_notes || 'Aucune note'}</p>
                    )}
                  </div>

                  {/* Actions */}
                  {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">Actions</h3>
                      <div className="flex flex-wrap gap-2">
                        {statusActions
                          .filter(a => a.status !== appointment.status)
                          .map(action => (
                            <button
                              key={action.status}
                              onClick={() => handleStatusChange(action.status)}
                              disabled={storeLoading}
                              className="px-3 py-1.5 border border-neutral-300 rounded-lg text-sm text-neutral-700 hover:bg-neutral-100 flex items-center gap-1.5"
                            >
                              {action.icon} {action.label}
                            </button>
                          ))}
                        {!showReschedule && appointment.appointment_type_id && (
                          <button onClick={handleRescheduleOpen}
                            className="px-3 py-1.5 border border-blue-300 rounded-lg text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-1.5">
                            <CalendarClock className="h-4 w-4" /> Replanifier
                          </button>
                        )}
                        {!showCancelConfirm && (
                          <button onClick={() => { setShowCancelConfirm(true); setShowReschedule(false); }}
                            className="px-3 py-1.5 border border-red-300 rounded-lg text-sm text-red-600 hover:bg-red-50 flex items-center gap-1.5">
                            <XCircle className="h-4 w-4" /> Annuler le RDV
                          </button>
                        )}
                      </div>

                      {/* Reschedule */}
                      {showReschedule && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-blue-900 flex items-center gap-2">
                              <CalendarClock className="h-4 w-4" /> Replanifier
                            </h4>
                            <button onClick={() => { setShowReschedule(false); setRescheduleSlot(null); }} className="text-blue-400 hover:text-blue-600">
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="space-y-4">
                            <div className="flex items-center gap-3">
                              <Calendar className="h-5 w-5 text-blue-400" />
                              <input type="date" value={rescheduleDate} onChange={handleRescheduleDateChange}
                                min={format(new Date(), 'yyyy-MM-dd')}
                                className="px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF] text-sm" />
                            </div>
                            <SlotPicker slots={availableSlots} loading={slotsLoading} selectedSlot={rescheduleSlot}
                              onSelectSlot={setRescheduleSlot} selectedDate={new Date(rescheduleDate + 'T00:00:00')} />
                            {rescheduleSlot && (
                              <div className="p-3 bg-white rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-900 mb-3">
                                  Replanifier au <strong>{format(parseISO(rescheduleSlot.start), "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })}</strong> ?
                                </p>
                                <div className="flex justify-end gap-2">
                                  <button onClick={() => setRescheduleSlot(null)} className="px-3 py-1.5 text-neutral-600 text-sm">Changer</button>
                                  <button onClick={handleRescheduleConfirm} disabled={storeLoading}
                                    className="px-4 py-1.5 bg-[#0066FF] text-white rounded-lg text-sm hover:bg-[#0052CC] disabled:opacity-50 flex items-center gap-1.5">
                                    {storeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                                    Confirmer
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Cancel confirm */}
                      {showCancelConfirm && (
                        <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-sm text-red-700 mb-3">Etes-vous sur de vouloir annuler ce rendez-vous?</p>
                          <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg mb-3 text-sm" placeholder="Raison de l'annulation (optionnel)" />
                          <div className="flex justify-end gap-2">
                            <button onClick={() => { setShowCancelConfirm(false); setCancelReason(''); }}
                              className="px-3 py-1.5 text-neutral-600 text-sm">Non, garder</button>
                            <button onClick={handleCancel} disabled={storeLoading}
                              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50">
                              {storeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Oui, annuler'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {appointment.status === 'cancelled' && appointment.cancellation_reason && (
                    <div className="bg-red-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-2">Raison de l'annulation</h3>
                      <p className="text-sm text-red-700">{appointment.cancellation_reason}</p>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
